import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { ScoreOverview } from '@/components/score/ScoreOverview'
import { DimensionOverview } from '@/components/score/DimensionOverview'
import { RecommendationsWidget } from '@/components/recommendations/RecommendationsWidget'
import { RedFlagsPanel } from '@/components/alerts/RedFlagsPanel'
import { AlertsWidget } from '@/components/alerts/AlertsWidget'
import { BadgeWidget } from '@/components/badges/BadgeWidget'
import { MobileOptimizedLayout, MobileCard, MobileGrid, MobileStack, MobileHeader, MobileTabs } from '@/components/layout/MobileOptimizedLayout'
import { PullToRefresh } from '@/components/ui/PullToRefresh'
import { TLBHScore, Recommendation, Alert, Badge } from '@/types'

interface MobileDashboardProps {
  score: TLBHScore
  recommendations: Recommendation[]
  alerts: Alert[]
  badges: Badge[]
  previousScore?: number
}

export function MobileDashboard({ 
  score, 
  recommendations, 
  alerts, 
  badges, 
  previousScore 
}: MobileDashboardProps) {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')

  const unreadAlerts = alerts.filter(alert => !alert.read)
  const earnedBadges = badges.filter(badge => badge.earnedAt)
  const activeRedFlags = score.redFlags.filter(flag => flag.status === 'active')

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'alerts', label: 'Alerts', count: unreadAlerts.length },
    { id: 'recommendations', label: 'Actions', count: recommendations.filter(r => r.status === 'pending').length },
    { id: 'badges', label: 'Badges', count: earnedBadges.length }
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <MobileStack spacing="md">
            {/* Score Overview */}
            <ScoreOverview 
              score={score}
              previousScore={previousScore}
              showTrend={true}
              layout="vertical"
            />

            {/* Key Metrics Cards */}
            <MobileGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap="sm">
              <MobileCard padding="sm">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {score.tlbhScore}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                    TLBH Score
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    +2.5 this month
                  </div>
                </div>
              </MobileCard>

              <MobileCard padding="sm">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {score.confidence.toFixed(0)}%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Confidence
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    High
                  </div>
                </div>
              </MobileCard>

              <MobileCard padding="sm">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {activeRedFlags.length}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Red Flags
                  </div>
                  <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    {activeRedFlags.length > 0 ? 'Needs attention' : 'All clear'}
                  </div>
                </div>
              </MobileCard>

              <MobileCard padding="sm">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {earnedBadges.length}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Badges
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Earned
                  </div>
                </div>
              </MobileCard>
            </MobileGrid>

            {/* Recent Activity */}
            <MobileCard>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Recent Activity
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Score updated
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Your TLBH score increased by 2.5 points
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      New recommendation
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Improve GST filing consistency
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">1 day ago</p>
                  </div>
                </div>
              </div>
            </MobileCard>
          </MobileStack>
        )

      case 'dimensions':
        return (
          <MobileCard>
            <DimensionOverview 
              dimensions={score.dimensionScores}
              defaultView="list"
            />
          </MobileCard>
        )

      case 'alerts':
        return (
          <MobileStack spacing="md">
            <RedFlagsPanel 
              redFlags={score.redFlags}
              alerts={alerts}
              showFilters={false}
            />
            <AlertsWidget 
              alerts={alerts}
              maxItems={10}
            />
          </MobileStack>
        )

      case 'recommendations':
        return (
          <RecommendationsWidget 
            recommendations={recommendations}
            maxItems={10}
          />
        )

      case 'badges':
        return (
          <BadgeWidget 
            badges={badges}
            maxItems={10}
            showViewAll={false}
          />
        )

      default:
        return null
    }
  }

  const handleRefresh = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    // In real app, this would refresh the data
    console.log('Refreshing dashboard data...')
  }

  return (
    <MobileOptimizedLayout>
      <PullToRefresh onRefresh={handleRefresh}>
        <MobileStack spacing="lg">
        {/* Welcome Header */}
        <MobileHeader
          title={`Welcome back, ${user?.name}!`}
          subtitle="Here's your business health overview"
        />

        {/* Mobile Navigation Tabs */}
        <MobileTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        {renderTabContent()}

        {/* Quick Actions - Always visible */}
        <MobileCard>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <MobileGrid columns={{ mobile: 1, tablet: 2, desktop: 3 }} gap="sm">
            <button className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
              <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                Update Business Info
              </h4>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                Keep your details current
              </p>
            </button>
            <button className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
              <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                View Recommendations
              </h4>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                Improve your score
              </p>
            </button>
            <button className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left sm:col-span-2 lg:col-span-1">
              <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                Download Report
              </h4>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                Export your report
              </p>
            </button>
          </MobileGrid>
        </MobileCard>
        </MobileStack>
      </PullToRefresh>
    </MobileOptimizedLayout>
  )
}